import { type InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { IoCameraOutline, IoCropOutline } from 'react-icons/io5';
import { CloseButton } from '@mantine/core';
import ImageCropModal from './ImageCropModal';
import './FileInput.css';

export type FileInputValue = File | { id: number; url: string };

export interface FileInputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'className' | 'onChange' | 'value' | 'multiple' | 'onBlur'
  > {
  error?: string;
  className?: string;
  value?: FileInputValue | FileInputValue[] | null;
  multiple?: boolean;
  label?: string;
  description?: string;
  /** Подсказка про перетаскивание порядка (для редактирования) */
  reorderHint?: string;
  /** Превью квадратные; клик по новому фото открывает выбор квадратной области */
  cropSquare?: boolean;
  onChange?: (files: FileInputValue | FileInputValue[] | null) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

const FILE_PREVIEW_TYPE = 'FILE_INPUT_PREVIEW';

function arrayMove<T>(arr: T[], from: number, to: number): T[] {
  const copy = [...arr];
  const [removed] = copy.splice(from, 1);
  copy.splice(to, 0, removed);
  return copy;
}

function previewKey(file: FileInputValue, index: number): string {
  if (file instanceof File) {
    return `file-${file.name}-${file.size}-${file.lastModified}-${index}`;
  }
  return `existing-${file.id}`;
}

interface ImagePreviewItemProps {
  file: FileInputValue;
  index: number;
  onRemove: () => void;
  onMove: (dragIndex: number, dropIndex: number) => void;
  enableReorder: boolean;
  square: boolean;
  onCrop?: () => void;
}

function ImagePreviewItem({
  file,
  index,
  onRemove,
  onMove,
  enableReorder,
  square,
  onCrop,
}: ImagePreviewItemProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(file.url);
  }, [file]);

  const [{ isDragging }, drag] = useDrag({
    type: FILE_PREVIEW_TYPE,
    item: () => ({ index }),
    canDrag: enableReorder,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: FILE_PREVIEW_TYPE,
    canDrop: () => enableReorder,
    drop: (item: { index: number }) => {
      if (item.index !== index) {
        onMove(item.index, index);
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) }),
  });

  if (enableReorder) {
    drag(drop(ref));
  }

  return (
    <div
      ref={ref}
      onClick={onCrop}
      title={onCrop ? 'Нажмите, чтобы выбрать квадратную область' : undefined}
      className={`file-input__preview-item ${square ? 'file-input__preview-item--square' : ''} ${onCrop ? 'file-input__preview-item--croppable' : ''} ${enableReorder ? 'file-input__preview-item--draggable' : ''} ${isDragging ? 'file-input__preview-item--dragging' : ''} ${isOver ? 'file-input__preview-item--drop-target' : ''}`}
    >
      {previewUrl && <img src={previewUrl} alt={`Превью ${index + 1}`} />}
      {onCrop ? (
        <button
          type="button"
          className="file-input__crop-btn"
          aria-label="Выбрать квадратную область"
          onClick={(e) => {
            e.stopPropagation();
            onCrop();
          }}
        >
          <IoCropOutline />
        </button>
      ) : null}
      <CloseButton
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="file-input__remove-btn"
      />
    </div>
  );
}

function FileInputInner({
  error,
  className = '',
  value,
  id,
  multiple = false,
  label,
  description,
  reorderHint,
  cropSquare = false,
  onChange,
  onBlur,
  ...props
}: FileInputProps) {
  const [cropIndex, setCropIndex] = useState<number | null>(null);
  const inputId = id || `file-input-${Math.random().toString(36).substr(2, 9)}`;
  const inputClass = `file-input ${error ? 'file-input--error' : ''}`.trim();
  const buttonClass =
    `file-input__button ${error ? 'file-input__button--error' : ''} ${className}`.trim();

  const files = Array.isArray(value) ? value : value ? [value] : [];
  const enableReorder = Boolean(multiple && onChange && files.length > 1);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!onChange) return;

    if (multiple) {
      onChange([...files, ...selectedFiles]);
    } else {
      onChange(selectedFiles[0] || null);
    }
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    if (multiple) {
      onChange?.(files.filter((_, i) => i !== index));
    } else {
      onChange?.(null);
    }
  };

  const moveImage = (dragIndex: number, dropIndex: number) => {
    if (!multiple || !onChange) return;
    onChange(arrayMove(files, dragIndex, dropIndex));
  };

  const cropTarget = cropIndex != null ? files[cropIndex] : undefined;
  const cropFile = cropTarget instanceof File ? cropTarget : null;

  const applyCrop = (cropped: File) => {
    if (cropIndex == null || !onChange) return;
    if (multiple) {
      onChange(files.map((file, i) => (i === cropIndex ? cropped : file)));
    } else {
      onChange(cropped);
    }
    setCropIndex(null);
  };

  const hasLocalFiles = files.some((file) => file instanceof File);
  const hasFiles = files.length > 0;
  const containerClass =
    `file-input__container ${hasFiles ? 'file-input__container--has-content' : ''}`.trim();

  return (
    <div className="file-input-wrapper">
      {label && <label className="file-input__label">{label}</label>}
      {description && <p className="file-input__description">{description}</p>}
      {enableReorder && reorderHint ? (
        <p className="file-input__reorder-hint">{reorderHint}</p>
      ) : null}
      {cropSquare && hasLocalFiles ? (
        <p className="file-input__reorder-hint">
          Нажмите на новое фото, чтобы выбрать квадратную область. Без выбора фото обрежется по центру.
        </p>
      ) : null}
      <div className={containerClass}>
        <div className="file-input__previews">
          {hasFiles &&
            files.map((file, index) => (
              <ImagePreviewItem
                key={previewKey(file, index)}
                file={file}
                index={index}
                onRemove={() => removeImage(index)}
                onMove={moveImage}
                enableReorder={enableReorder}
                square={cropSquare}
                onCrop={
                  cropSquare && onChange && file instanceof File
                    ? () => setCropIndex(index)
                    : undefined
                }
              />
            ))}
          <label
            htmlFor={inputId}
            className={`${buttonClass} ${cropSquare ? 'file-input__button--square' : ''}`.trim()}
          >
            <div className="file-input__icon-wrapper">
              <IoCameraOutline />
            </div>
          </label>
          <input
            id={inputId}
            type="file"
            className={inputClass}
            onChange={handleChange}
            onBlur={onBlur}
            multiple={multiple}
            {...props}
          />
        </div>
      </div>
      {error && <span className="file-input__error-message">{error}</span>}
      {cropSquare ? (
        <ImageCropModal file={cropFile} onClose={() => setCropIndex(null)} onApply={applyCrop} />
      ) : null}
    </div>
  );
}

export default function FileInput(props: FileInputProps) {
  return (
    <DndProvider backend={HTML5Backend}>
      <FileInputInner {...props} />
    </DndProvider>
  );
}
